/**
 * Plata, moneda y links a Meta de las respuestas de Meta Ads (para E2 en adelante).
 * Funciones puras. Ver docs/maps/meta-ads.md § Moneda.
 *
 * Regla: los montos van en la moneda de la cuenta y **nunca se suman monedas
 * distintas**. Con `mixed_currencies: true` la API manda la plata del total en
 * `null` (los conteos sí) y el detalle por moneda en `account_totals_by_currency`
 * (getmetacampaigns), `totals_by_currency` (getunitcampaigns) o `by_currency`
 * (getcampaignmetrics / getcampaignbreakdowns).
 */

/** Campos de plata del bloque `metrics` (los `cost_per_*` se detectan por prefijo). */
export const META_MONEY_FIELDS = ['spend'];

const isMoneyField = (key) => META_MONEY_FIELDS.includes(key) || key.startsWith('cost_per_');

const currencyCode = (value) => (typeof value === 'string' && /^[A-Z]{3}$/.test(value.trim().toUpperCase())
  ? value.trim().toUpperCase()
  : null);

/**
 * Copia del bloque con la plata en `null` (los conteos quedan). Defensa por si
 * un total mezclado llegara con montos: nunca se muestran.
 *
 * @param {object|null} metrics
 * @returns {object|null}
 */
export function stripMetaMoney(metrics) {
  if (!metrics || typeof metrics !== 'object') return metrics ?? null;
  return Object.fromEntries(Object.entries(metrics).map(([k, v]) => [k, isMoneyField(k) ? null : v]));
}

/**
 * Fila por moneda con forma única. Acepta las dos formas del contrato:
 * métricas inline (`{ currency, spend, …, ads_count }`) o anidadas
 * (`{ currency, metrics, daily, ads_count }`).
 *
 * @param {object} row
 * @returns {{ currency: string|null, metrics: object, daily: Array|null, adsCount: number|null }}
 */
export function normalizeMetaCurrencyRow(row) {
  const data = row && typeof row === 'object' ? row : {};
  const { currency, metrics, daily, ads_count: adsCount, ...inline } = data;
  return {
    currency: currencyCode(currency),
    metrics: metrics && typeof metrics === 'object' ? metrics : inline,
    daily: Array.isArray(daily) ? daily : null,
    adsCount: Number.isFinite(Number(adsCount)) && adsCount !== null ? Number(adsCount) : null,
  };
}

/**
 * Totales listos para mostrar: con una moneda, `total` con plata; mezclado,
 * `total` sin plata (solo conteos) y la plata en `byCurrency`.
 *
 * @param {{ total?: object|null, currency?: string|null, mixedCurrencies?: boolean,
 *   byCurrency?: Array|null }} input
 * @returns {{ mixed: boolean, currency: string|null, total: object|null,
 *   byCurrency: Array<{ currency: string|null, metrics: object, daily: Array|null, adsCount: number|null }> }}
 */
export function resolveMetaTotals({ total = null, currency = null, mixedCurrencies = false, byCurrency = null } = {}) {
  const rows = Array.isArray(byCurrency) ? byCurrency.map(normalizeMetaCurrencyRow) : [];
  const mixed = mixedCurrencies === true || new Set(rows.map((r) => r.currency).filter(Boolean)).size > 1;
  if (mixed) return { mixed: true, currency: null, total: stripMetaMoney(total), byCurrency: rows };
  const single = currencyCode(currency) || rows[0]?.currency || currencyCode(total?.currency) || null;
  return { mixed: false, currency: single, total: total ?? null, byCurrency: rows };
}

/**
 * Lee los totales de cada respuesta con plata.
 *
 * @param {'campaigns'|'unit'|'metrics'|'breakdowns'} kind Ruta de origen.
 * @param {object} data `data` de la respuesta.
 * @returns {ReturnType<typeof resolveMetaTotals>}
 */
export function readMetaTotals(kind, data) {
  const d = data && typeof data === 'object' ? data : {};
  if (kind === 'campaigns') {
    return resolveMetaTotals({
      total: d.account_totals, currency: d.currency ?? d.account_totals?.currency,
      mixedCurrencies: d.mixed_currencies, byCurrency: d.account_totals_by_currency,
    });
  }
  if (kind === 'unit') {
    return resolveMetaTotals({
      total: d.totals, currency: d.currency ?? d.totals?.currency,
      mixedCurrencies: d.mixed_currencies, byCurrency: d.totals_by_currency,
    });
  }
  return resolveMetaTotals({
    total: d.metrics ?? null, currency: d.currency, mixedCurrencies: d.mixed_currencies, byCurrency: d.by_currency,
  });
}

/**
 * Monto en su moneda (`ARS 114.150`). Sin monto o sin moneda → `—`.
 * Nunca convierte.
 *
 * @param {number|null} amount
 * @param {string|null} currency
 * @param {{ fixed2?: boolean }} [options] `fixed2`: siempre 2 decimales (CPC/CPM).
 * @returns {string}
 */
export function formatMetaMoney(amount, currency, { fixed2 = false } = {}) {
  const code = currencyCode(currency);
  const n = Number(amount);
  if (amount == null || !code || !Number.isFinite(n)) return '—';
  try {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: code,
      currencyDisplay: 'code',
      // `fixed2` (ajuste 10, CPC/CPM): siempre 2 decimales como Meta; el gasto queda igual.
      ...(fixed2 ? { minimumFractionDigits: 2, maximumFractionDigits: 2 } : { maximumFractionDigits: n >= 100 ? 0 : 2 }),
    }).format(n).replace(/\u00a0/g, ' ');
  } catch {
    return `${code} ${n}`;
  }
}

/**
 * Link «Ver en Meta» (puede ser `null`) e id de cuenta de un objeto de la API.
 * Solo https: otro esquema se descarta.
 *
 * @param {object} item Cuenta, campaña o anuncio.
 * @returns {{ metaUrl: string|null, adAccountId: string|null }}
 */
export function readMetaLinks(item) {
  const raw = typeof item?.meta_url === 'string' ? item.meta_url.trim() : '';
  let metaUrl = null;
  try {
    metaUrl = raw && new URL(raw).protocol === 'https:' ? raw : null;
  } catch {
    metaUrl = null;
  }
  const account = item?.ad_account_id == null ? '' : String(item.ad_account_id).trim().replace(/^act_/, '');
  return { metaUrl, adAccountId: account || null };
}
